import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { documentListPath, useApi } from "../api/client";
import type { DocumentListItem, DocumentListResponse } from "../api/types";
import { ConfidenceBadge } from "../components/ConfidenceBadge";
import { CostCell } from "../components/CostCell";
import { Pagination } from "../components/Pagination";
import { Empty, ErrorState, Loading } from "../components/States";
import { SubmitterFilter } from "../components/SubmitterFilter";
import {
  Breadcrumb,
  CustomRange,
  FilterPanel,
  SegmentedControl,
  Toolbar,
  type Segment,
} from "../components/Toolbar";
import { downloadCsv, stampedName, toCsv } from "../lib/csv";
import {
  EM_DASH,
  fmtDateTime,
  fmtMs,
  fmtTokens,
  humanise,
} from "../lib/format";

const TYPES = [
  "trade_licence",
  "bank_statement",
  "emirates_id",
  "passport",
  "address_proof",
  "invoice",
  "bill",
  "moa",
  "tax_registration",
  "certificate_of_incorporation",
  "unknown",
];

const STATUSES = ["completed", "processing", "queued", "failed"];

/**
 * The same bands the agent page's confidence histogram is drawn from, in the
 * same order, so a bar and this dropdown cannot describe different things.
 *
 * "Unscored" is a band rather than 0%: those extractions came from the regex
 * fallback, which reports no confidence at all. Offering it here matters more
 * than it looks — it is the one filter that finds documents processed without
 * an agent.
 */
const CONFIDENCE_BANDS = [
  { value: "0-50", label: "0–50% — lowest" },
  { value: "50-70", label: "50–70%" },
  { value: "70-85", label: "70–85%" },
  { value: "85-95", label: "85–95%" },
  { value: "95-100", label: "95–100%" },
  { value: "unscored", label: "Unscored — no agent confidence" },
];

export type PeriodKey = "all" | "last_30d" | "last_60d" | "last_90d" | "custom";

export const PERIOD_SEGMENTS: Segment<PeriodKey>[] = [
  { key: "all", label: "All" },
  { key: "last_30d", label: "Last 30d" },
  { key: "last_60d", label: "Last 60d" },
  { key: "last_90d", label: "Last 90d" },
  { key: "custom", label: "Custom" },
];

export function DocumentList() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const page = Number(params.get("page") ?? 1);
  const pageSize = Number(params.get("page_size") ?? 25);
  const docType = params.get("document_type") ?? "";
  const status = params.get("status") ?? "";
  const applicationId = params.get("application_id") ?? "";
  // Arrives from the Formats tab's "review" link. There is no control for
  // it in the filter panel -- a uuid is not something anyone types -- so it
  // is read from the URL and cleared from the chip below.
  const formatId = params.get("format_id") ?? "";
  const confidenceBand = params.get("confidence_band") ?? "";
  const submittedBy = params.get("submitted_by") ?? "";
  const period = (params.get("period") ?? "all") as PeriodKey;
  const dateFrom = params.get("date_from") ?? "";
  const dateTo = params.get("date_to") ?? "";

  const [q, setQ] = useState(params.get("q") ?? "");
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Every filter lives in the URL, so a filtered view is shareable and
  // survives a refresh. The search box is the exception: it is debounced into
  // the URL rather than written on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (q) next.set("q", q);
      else next.delete("q");
      if (next.get("q") !== params.get("q")) {
        next.set("page", "1");
        setParams(next, { replace: true });
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.set("page", "1");
    setParams(next);
  };

  const activeFilters = [
    docType,
    status,
    applicationId,
    formatId,
    confidenceBand,
    submittedBy,
    period !== "all" ? period : "",
  ].filter(Boolean).length;

  const ready = period !== "custom" || (dateFrom !== "" && dateTo !== "");

  const { data, error, loading, reload } = useApi<DocumentListResponse>(
    ready
      ? documentListPath({
          page,
          page_size: pageSize,
          document_type: docType || undefined,
          status: status || undefined,
          q: params.get("q") || undefined,
          application_id: applicationId || undefined,
          format_id: formatId || undefined,
          confidence_band: confidenceBand || undefined,
          submitted_by: submittedBy || undefined,
          period,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          sort: "created_at",
          order: "desc",
        })
      : null,
  );

  const exportCsv = () => {
    if (!data?.items.length) return;
    downloadCsv(
      stampedName("documents"),
      toCsv<DocumentListItem>(data.items, [
        { header: "Document ID", value: (r) => r.document_id },
        { header: "Filename", value: (r) => r.original_filename },
        { header: "Type", value: (r) => r.doc_type },
        { header: "Application", value: (r) => r.application_id },
        { header: "Status", value: (r) => r.status },
        { header: "Submitted by", value: (r) => r.submitted_by_username },
        { header: "Via API", value: (r) => (r.submitted_via_api ? "yes" : "no") },
        {
          // The agent's confidence and the gate score are separate columns
          // rather than one coalesced figure. They measure different things,
          // and collapsing them meant a spreadsheet could average a gate score
          // against an agent confidence without anything saying they were not
          // the same quantity.
          header: "Agent confidence %",
          value: (r) => r.agent_confidence ?? r.confidence,
        },
        { header: "Gate score %", value: (r) => r.gate_score },
        { header: "Gate action", value: (r) => r.gate_action },
        { header: "Required fields %", value: (r) => r.required_score },
        { header: "Required fields met", value: (r) => r.required_met },
        { header: "Required fields total", value: (r) => r.required_total },
        { header: "Processing ms", value: (r) => r.processing_ms },
        { header: "Tokens", value: (r) => r.total_tokens },
        // Blank, not 0: an unrecorded or unpriced cost is not zero cost, and a
        // spreadsheet would happily sum a zero into a wrong total.
        {
          header: "Cost USD",
          value: (r) => (r.usage_recorded ? r.cost_usd : null),
        },
        { header: "Processed", value: (r) => r.created_at },
      ]),
    );
  };

  return (
    <>
      <Breadcrumb
        trail={[{ label: "Agents", to: "/agents" }, { label: "Documents" }]}
      />

      <div className="page-head">
        <div>
          <h1 className="page-title">Documents</h1>
          <p className="page-sub">
            Every processed document. Select a row for the full extraction.
          </p>
        </div>
        <div className="range-group">
          <SubmitterFilter
            value={submittedBy}
            onChange={(v) => setParam("submitted_by", v)}
            idPrefix="doc"
          />
          {period === "custom" ? (
            <CustomRange
              from={dateFrom}
              to={dateTo}
              onFrom={(v) => setParam("date_from", v)}
              onTo={(v) => setParam("date_to", v)}
              idPrefix="doc"
            />
          ) : null}
          <SegmentedControl
            segments={PERIOD_SEGMENTS}
            value={period}
            onChange={(p) => {
              const next = new URLSearchParams(params);
              next.set("period", p);
              if (p !== "custom") {
                next.delete("date_from");
                next.delete("date_to");
              }
              next.set("page", "1");
              setParams(next);
            }}
            label="Time range"
          />
        </div>
      </div>

      {/* A format filter has no control in the panel to show it is on, so it
          says so here. Without this the page looks like the whole corpus is
          167 documents rather than one issuer's template, which is a quietly
          wrong impression to leave someone with. */}
      {formatId ? (
        <div className="notice">
          Showing one document format
          {data?.applied_format ? (
            <>
              {": "}
              <strong>
                {data.applied_format.label ||
                  [
                    data.applied_format.authority_label || "unidentified issuer",
                    data.applied_format.number_shape,
                    `${data.applied_format.field_count} fields`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
              </strong>
            </>
          ) : null}
          {data ? ` — ${data.total} document${data.total === 1 ? "" : "s"}.` : "."}{" "}
          <button className="link-btn" onClick={() => setParam("format_id", "")}>
            Show all documents
          </button>
        </div>
      ) : null}

      <Toolbar
        title="Document Extractions"
        subtitle={
          <>
            Per-document confidence, tokens and cost
            {applicationId
              ? ` · application ${applicationId.slice(0, 8)}…`
              : ""}
            {/* Named explicitly: arriving from a histogram bar, the list is
                already filtered and a reader who does not know that reads a
                subset as the whole set. */}
            {confidenceBand
              ? ` · confidence ${confidenceBand === "unscored" ? "unscored" : `${confidenceBand}%`}`
              : ""}
          </>
        }
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        filtersOpen={filtersOpen}
        activeFilterCount={activeFilters}
        onExport={exportCsv}
        exportDisabled={!data?.items.length}
        search={q}
        onSearch={setQ}
        searchPlaceholder="Search by filename"
      />

      {filtersOpen ? (
        <FilterPanel
          activeCount={activeFilters}
          onClear={() => {
            setQ("");
            setParams(new URLSearchParams());
            setFiltersOpen(false);
          }}
        >
          <label className="muted" htmlFor="f-type">
            Type
          </label>
          <select
            id="f-type"
            value={docType}
            onChange={(e) => setParam("document_type", e.target.value)}
          >
            <option value="">All types</option>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {humanise(t)}
              </option>
            ))}
          </select>

          <label className="muted" htmlFor="f-status">
            Status
          </label>
          <select
            id="f-status"
            value={status}
            onChange={(e) => setParam("status", e.target.value)}
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {humanise(s)}
              </option>
            ))}
          </select>

          {/* The bands the agent histogram links in with. Without a control
              here the filter was real but invisible: arriving from a bar
              filtered the list with nothing on screen saying so and no way to
              clear it short of editing the URL. */}
          <label
            className="muted"
            htmlFor="f-confidence"
            title="Filters on the agent's own confidence, the number shown in the Confidence column — not on the gate score printed beneath it."
          >
            Confidence
          </label>
          <select
            id="f-confidence"
            value={confidenceBand}
            onChange={(e) => setParam("confidence_band", e.target.value)}
          >
            <option value="">Any confidence</option>
            {CONFIDENCE_BANDS.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </select>
        </FilterPanel>
      ) : null}

      <div className="card">
        {!ready ? (
          <Empty
            title="Pick both dates"
            hint="A custom range needs a start and an end."
          />
        ) : loading ? (
          <Loading what="documents" />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : !data || data.items.length === 0 ? (
          <Empty title="No documents match" hint="Try clearing the filters." />
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col" className="si">
                      Sl
                    </th>
                    <th scope="col">Filename</th>
                    <th scope="col">Type</th>
                    <th scope="col">Application</th>
                    <th scope="col">Status</th>
                    <th scope="col">Submitted by</th>
                    <th
                      scope="col"
                      title="The agent's own confidence in its extraction — what the Confidence filter and the agent pages measure. The gate score below each badge is a different number."
                    >
                      Confidence
                    </th>
                    <th
                      scope="col"
                      className="num"
                      title="How many of the fields this agent is scored on were extracted from this document. Set which fields count on the agent's Fields tab."
                    >
                      Fields
                    </th>
                    <th scope="col" className="num">
                      Time
                    </th>
                    <th scope="col" className="num">
                      Tokens
                    </th>
                    <th scope="col" className="num">
                      Cost
                    </th>
                    <th scope="col">Processed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((d, i) => {
                    const open = () =>
                      navigate(`/documents/${d.job_id ?? d.document_id}`);
                    return (
                      <tr
                        key={d.document_id}
                        className="row-link"
                        onClick={open}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            open();
                          }
                        }}
                        tabIndex={0}
                        role="link"
                        aria-label={`Open ${d.original_filename ?? d.document_id}`}
                      >
                        {/* Continues across pages rather than restarting at 1. */}
                        <td className="si">
                          {(data.page - 1) * data.page_size + i + 1}
                        </td>
                        <td>
                          <div>
                            {d.original_filename ?? (
                              <span className="muted">{EM_DASH}</span>
                            )}
                          </div>
                          <div className="mono muted">
                            {d.document_id.slice(0, 8)}
                          </div>
                        </td>
                        <td className="nowrap">
                          {d.doc_type ? (
                            humanise(d.doc_type)
                          ) : (
                            <span className="muted">untyped</span>
                          )}
                        </td>
                        <td className="nowrap">
                          {d.application_id ? (
                            <Link
                              to={`/applications/${d.application_id}`}
                              className="mono"
                              onClick={(e) => e.stopPropagation()}
                              title={d.application_id}
                            >
                              {d.application_id.slice(0, 8)}…
                            </Link>
                          ) : (
                            <span className="muted">none</span>
                          )}
                        </td>
                        <td className="nowrap">
                          {humanise(d.status)}
                          {d.deduplicated ? (
                            <>
                              {" "}
                              <span
                                className="pill"
                                title="Served from the deduplication cache; this run consumed no tokens."
                              >
                                cached
                              </span>
                            </>
                          ) : null}
                        </td>
                        <td className="nowrap">
                          {d.submitted_by_username ? (
                            <>
                              {d.submitted_by_username}
                              {d.submitted_via_api ? (
                                <>
                                  {" "}
                                  <span
                                    className="pill"
                                    title="Submitted through the external API with a client credential"
                                  >
                                    API
                                  </span>
                                </>
                              ) : null}
                            </>
                          ) : (
                            <span
                              className="muted"
                              title="Submitted by an .env account, or before submitters were recorded"
                            >
                              not recorded
                            </span>
                          )}
                        </td>
                        <td>
                          {/* The agent's own confidence -- the same measurement
                              the Confidence filter and the agent page's
                              histogram are drawn from.

                              This column used to show the gate score, which is
                              why selecting 50-70% returned rows reading 86%.
                              They are two different numbers: the gate is 40%
                              OCR quality plus 60% a field-presence checklist,
                              so two non-empty fields earn a flat 60 points and
                              almost everything lands in the 80s. Filtering on
                              one while displaying the other made the filter
                              look broken when it was working.

                              The gate still appears underneath, because it is
                              what the routing decision was made on and the
                              detail page leads with it. */}
                          <ConfidenceBadge
                            value={d.agent_confidence ?? d.confidence}
                            absentReason="No confidence was recorded for this document"
                          />
                          {d.gate_score !== null &&
                          d.gate_score !== undefined ? (
                            <div
                              className="muted"
                              style={{ fontSize: 11, marginTop: 3 }}
                              title="Confidence gate: 40% document quality plus 60% deterministic field checks. This is the score the routing decision was made on, not the agent's own confidence."
                            >
                              gate {Math.round(d.gate_score * 10) / 10}%
                              {d.gate_action
                                ? ` · ${humanise(d.gate_action)}`
                                : ""}
                            </div>
                          ) : null}
                        </td>
                        <td className="num">
                          {/* Percentage above, the raw ratio below it. "80%"
                              alone hides whether that is 4 of 5 or 8 of 10, and
                              with five scored fields every value is a multiple
                              of 20 — the denominator is what makes it readable. */}
                          {d.required_score === null ? (
                            <span
                              className="muted"
                              title={
                                d.agent_confidence === null
                                  ? "No agent extraction on this document"
                                  : "No fields are scored for this agent yet"
                              }
                            >
                              {EM_DASH}
                            </span>
                          ) : (
                            <>
                              <span
                                className={
                                  d.required_score === 100
                                    ? "req-ok"
                                    : d.required_score >= 60
                                      ? "req-warn"
                                      : "req-bad"
                                }
                              >
                                {d.required_score}%
                              </span>
                              <div className="muted" style={{ fontSize: 11 }}>
                                {d.required_met}/{d.required_total}
                              </div>
                            </>
                          )}
                        </td>
                        <td
                          className="num"
                          title={
                            d.processing_ms
                              ? "How long the latest extraction attempt took, from queued to finished"
                              : undefined
                          }
                        >
                          {d.processing_ms ? fmtMs(d.processing_ms) : EM_DASH}
                        </td>
                        <td className="num">{fmtTokens(d.total_tokens)}</td>
                        <td className="num">
                          <CostCell
                            usd={d.cost_usd}
                            recorded={d.usage_recorded}
                          />
                        </td>
                        <td className="nowrap muted">
                          {fmtDateTime(d.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Pagination
              page={data.page}
              totalPages={data.total_pages}
              total={data.total}
              pageSize={data.page_size}
              noun="documents"
              onPage={(p) => setParam("page", String(p))}
              onPageSize={(n) => {
                const next = new URLSearchParams(params);
                next.set("page_size", String(n));
                next.set("page", "1");
                setParams(next);
              }}
            />
          </>
        )}
      </div>
    </>
  );
}
